"use client";

import { useRef } from "react";
import classNames from "classnames";

import { useSelector } from "@/redux/hooks";
import { getShowBanner } from "@/redux/selectors/header";
import { getUserAgent } from "@/redux/selectors/userAgent";

import ExposeOnScroll from "../Shared/Animated/ExposeOnScroll";
import { AdvertisingLink } from "./AdvertisingLink";
import { Banner } from "./Banner";
import Cart from "./Cart";
import { HamburgerButton } from "./HamburgerButton";
import { HeaderLinks } from "./HeaderLinks";
import { LocationIndicator } from "./LocationIndicator";
import { LocationPickerModal } from "./LocationPickerModal";
import { Logo } from "./Logo";
import { NotificationDot } from "./NotificationIndicator";
import { SearchBar } from "./SearchBar";
import { SearchBarSlim } from "./SearchBarSlim";
import SidebarNavigation from "./SidebarNavigation/SidebarNavigation";

export const Header: React.FC<{
  disableFixedPosition?: boolean;
  disableHeaderExposeScroll?: boolean;
  isAppBannerCookieDismissed?: boolean;
  minimal?: boolean;
}> = ({
  disableFixedPosition = false,
  disableHeaderExposeScroll = false,
  isAppBannerCookieDismissed = false,
  minimal = false,
}) => {
  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const locationIndicator = useRef<HTMLButtonElement>(null);
  const showBanner = useSelector(getShowBanner);
  const ref = useRef<HTMLDivElement>(null);
  const { isMobile } = useSelector(getUserAgent);
  const disableHeaderScroll =
    disableHeaderExposeScroll || disableFixedPosition || !isMobile;
  const headerLinksHeight = 36;

  /**
   * The following padding classes must be written out in their entirety even though
   * this appears extremely redundant and not DRY. Tailwind will only detect these
   * classes and include them in the production bundle if they are not dynamically generated.
   * See: https://v3.tailwindcss.com/docs/content-configuration#dynamic-class-names
   */

  const [[headerHeight, headerHeightLarge], [paddingClass, paddingClassLarge]] =
    minimal
      ? ([
          ["48x", "56px"],
          [
            "pt-[calc(var(--app-banner-height,0px)+48px)]",
            "lg:pt-[calc(var(--app-banner-height,0px)+56px)]",
          ],
        ] as const)
      : ([
          ["56px", "112px"],
          [
            "pt-[calc(var(--app-banner-height,0px)+52px+36px)]",
            "lg:pt-[calc(var(--app-banner-height,0px)+106px)]",
          ],
        ] as const);

  return (
    <div
      className={classNames(
        "transition-[padding-top] motion-reduce:transition-none",
        {
          [paddingClass]: !disableFixedPosition,
          "pt-0": disableFixedPosition,
        },
        paddingClassLarge,
      )}
      ref={ref}
    >
      <div
        /**
         * The transitions defined in the CSS below are not just for fun! The metrics
         * that Google collects on Cumulative Layout Shift counts the change in the
         * header when scrolling up and down as Layout Shift, even though it is
         * technically triggered by user interaction; the scroll event is not included
         * in the list of user events that can trigger document reflow.
         * See further documentation here: https://wicg.github.io/layout-instability/#sec-input-exclusion
         */
        className={classNames("z-header w-full bg-white top-0", {
          collapsed: minimal,
          "fixed transition-transform motion-reduce:transition-none transform lg:transform-none":
            !disableFixedPosition,
          "relative lg:fixed": disableFixedPosition,
          "translate-y-[-82px]":
            !disableFixedPosition && (!showBanner || minimal),
        })}
        data-testid="header"
        id="header-container"
      >
        <Banner
          disableFixedPosition={disableFixedPosition}
          isAppBannerCookieDismissed={isAppBannerCookieDismissed}
          minimal={minimal}
        />

        <header className="shadow-low relative">
          <div className="container pt-lg">
            <div className="flex flex-wrap justify-between items-center">
              <div className="flex">
                <HamburgerButton ref={hamburgerRef}>
                  <div className="absolute top-[1px] right-[1px]">
                    <NotificationDot data-testid="notification-dot" />
                  </div>
                </HamburgerButton>
                <Logo />
                <AdvertisingLink />
              </div>
              <div className="hidden lg:block flex-1">
                {!minimal && <SearchBar />}
              </div>
              <div className="flex items-center justify-center">
                {!minimal && <SearchBarSlim />}
                {!minimal && <LocationIndicator ref={locationIndicator} />}
                <Cart />
              </div>
            </div>
            {!minimal && (
              <ExposeOnScroll disabled={disableHeaderScroll} defaultHeight={48}>
                <HeaderLinks isMobile={isMobile} />
              </ExposeOnScroll>
            )}
          </div>
        </header>
      </div>

      <LocationPickerModal returnFocusRef={locationIndicator} />
      <SidebarNavigation returnFocusRef={hamburgerRef} />

      {/**
       * These CSS variables are here to allow other components to reference
       * the height of the header when making calculations. These are NOT safe
       * to access from components that are used in App Router that are visible
       * on page load because the variables will only be set during client-side
       * hydration. If you must reference these, ensure that you're not
       * inadvertently adding cumulative layout shift penalty on page load.
       */}
      <style jsx global>{`
        :root {
          --header-height: calc(${headerHeight} + ${headerLinksHeight}px);
        }

        @media (min-width: 1025px) {
          :root {
            --header-height: ${headerHeightLarge};
          }
        }
      `}</style>
    </div>
  );
};
